import type { CustomEmployeeField } from '@/app/features/control-panel/organization';
import {
  CUSTOM_FIELDS_KEY,
  useActiveCustomFields,
} from './custom-fields.helpers';
import { DatePicker, Form, Input, Select, Switch } from 'antd';
import dayjs from 'dayjs';

/**
 * The control a field type is filled in with.
 *
 * `Form.Item` injects `value` and `onChange` into its child, so this component
 * has to pass them straight through — keeping them here would leave the input
 * uncontrolled: the typing would show on screen but never reach the form.
 */
const FieldControl = ({
  field,
  ...control
}: { field: CustomEmployeeField } & Record<string, any>) => {
  switch (field.field_type) {
    case 'DATE':
      return (
        <DatePicker {...control} className="w-full" placeholder="Select date" />
      );
    case 'SELECT':
      return (
        <Select
          {...control}
          allowClear
          placeholder="Select"
          options={(field.options || []).map((option) => ({
            label: option,
            value: option,
          }))}
        />
      );
    case 'SWITCH':
      return <Switch {...control} className="custom-switch" />;
    default:
      return (
        <Input
          {...control}
          placeholder={`Enter ${field.field_name.toLowerCase()}`}
        />
      );
  }
};

const isDate = (field: CustomEmployeeField) => field.field_type === 'DATE';

const FieldItem = ({ field }: { field: CustomEmployeeField }) => (
  <Form.Item
    key={field.id}
    label={field.field_name}
    name={[CUSTOM_FIELDS_KEY, field.field_name]}
    // A switch reports its state through `checked`, not `value`.
    valuePropName={field.field_type === 'SWITCH' ? 'checked' : undefined}
    // Dates are stored as plain `YYYY-MM-DD` so the value survives the API,
    // while the picker still receives the dayjs object it needs.
    getValueProps={
      isDate(field)
        ? (value) => ({ value: value ? dayjs(value as string) : undefined })
        : undefined
    }
    normalize={
      isDate(field)
        ? (value) => (value ? dayjs(value).format('YYYY-MM-DD') : value)
        : undefined
    }
    rules={[
      { required: field.is_required, message: `Please enter ${field.field_name}` },
    ]}
  >
    <FieldControl field={field} />
  </Form.Item>
);

/**
 * The extra fields a company defined in Control Panel → Organization → Custom
 * Fields. They render as one block of their own, never mixed into the built-in
 * Personal / Job / Payroll sections, so an admin can always tell them apart.
 *
 * A company that defined nothing gets nothing: the component renders `null`, so
 * the employee forms stay exactly as they are today.
 */
const CustomFieldInputs = () => {
  const { fields } = useActiveCustomFields();
  if (!fields.length) return null;

  return (
    <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
      {fields.map((field) => (
        <FieldItem key={field.id} field={field} />
      ))}
    </div>
  );
};

export default CustomFieldInputs;
